import type { SVGProps } from 'react';

/**
 * One small stroke-icon set, drawn on a 24px grid. It replaces the emoji that used to stand in for
 * icons: emoji are drawn by the operating system, so they changed size, weight and colour between
 * Windows, Android and a thermal-printer kiosk, and could not follow the theme. These inherit
 * `currentColor`, scale with the font and stay decorative (aria-hidden) unless a title is given.
 */

export type IconName =
  | 'till'
  | 'box'
  | 'stock'
  | 'truck'
  | 'users'
  | 'return'
  | 'upload'
  | 'chart'
  | 'settings'
  | 'pulse'
  | 'menu'
  | 'close'
  | 'sun'
  | 'moon'
  | 'monitor'
  | 'cart'
  | 'inbox'
  | 'alert'
  | 'search'
  | 'barcode'
  | 'camera'
  | 'logout'
  | 'user'
  | 'check'
  | 'lock'
  | 'store'
  | 'receipt'
  | 'star'
  | 'pause'
  | 'plus'
  | 'minus'
  | 'scale'
  | 'tag'
  | 'message'
  | 'undo'
  | 'trash'
  | 'printer';

const PATHS: Record<IconName, string> = {
  // a receipt with a torn edge: the till
  till: 'M6 3h12v18l-2.25-1.5L13.5 21l-1.5-1.5L10.5 21l-2.25-1.5L6 21zM9 8h6M9 12h6M9 16h3',
  star: 'M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.9L12 16.9 6.7 19.7l1.1-5.9L3.5 9.7l5.9-.8z',
  pause: 'M8 5v14M16 5v14',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  // a hanging pan scale: loose goods
  scale: 'M4 20h16M12 4v3M6 7h12l-2 6a4 4 0 0 1-8 0zM8 13a4 4 0 0 0 8 0',
  tag: 'M3 12V4h8l10 10-8 8zM7.5 8.5h.01',
  message: 'M4 5h16v11H9l-5 4z',
  undo: 'M9 14 4 9l5-5M4 9h11a5 5 0 0 1 0 10h-4',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 12h10l1-12M9 7V4h6v3',
  printer: 'M7 9V3h10v6M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2M7 14h10v7H7z',
  receipt: 'M6 3h12v18l-2.25-1.5L13.5 21l-1.5-1.5L10.5 21l-2.25-1.5L6 21zM9 8h6M9 12h6M9 16h3',
  box: 'M21 8 12 3 3 8v8l9 5 9-5zM3 8l9 5 9-5M12 13v8',
  stock: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  truck: 'M3 6h11v10H3zM14 9h4l3 3v4h-7zM7.5 19.5a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5zM17.5 19.5a1.75 1.75 0 1 0 0-3.5 1.75 1.75 0 0 0 0 3.5z',
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 20a6.5 6.5 0 0 1 13 0M16 4.3a3.5 3.5 0 0 1 0 6.4M18 14.2A6.5 6.5 0 0 1 21.5 20',
  return: 'M9 14 4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3',
  upload: 'M12 16V4M7 9l5-5 5 5M4 15v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4',
  chart: 'M4 4v16h16M8 16v-4M12 16V8M16 16v-6',
  settings:
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.56-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34h.01a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87v.01a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.56 1.03z',
  pulse: 'M3 12h4l2.5-6 4 12 2.5-6H21',
  menu: 'M4 7h16M4 12h16M4 17h16',
  close: 'M6 6l12 12M18 6 6 18',
  sun: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4',
  moon: 'M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z',
  monitor: 'M3 5h18v11H3zM8 20h8M12 16v4',
  cart: 'M3 4h2.5l2 11h10.5l2-8H6.5M9.5 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM17 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  inbox: 'M3 13l2.5-8h13L21 13M3 13v6h18v-6M3 13h5l1 2.5h6l1-2.5h5',
  alert: 'M12 3 2 20h20zM12 10v4M12 17.5v.01',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4',
  barcode: 'M3 5v14M6 5v14M10 5v14M13 5v14M17 5v14M21 5v14',
  camera: 'M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1zM12 16.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  logout: 'M9 4H5a1 1 0 0 0-1 1v14a1 1 0 0 0 1 1h4M16 8l4 4-4 4M20 12H9',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0',
  check: 'M5 12.5 10 17.5 19 7',
  lock: 'M6 11h12v9H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  store: 'M4 9l1.5-5h13L20 9M4 9v11h16V9M4 9a2.67 2.67 0 0 0 5.33 0 2.67 2.67 0 0 0 5.34 0A2.67 2.67 0 0 0 20 9M10 20v-5h4v5',
};

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  /** Size in px (square). Defaults to 20. */
  size?: number;
  /** When given the icon is exposed to assistive tech with this label; otherwise it is decorative. */
  title?: string;
  strokeWidth?: number;
}

export function Icon({ name, size = 20, title, strokeWidth = 1.75, className, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      aria-label={title}
      focusable="false"
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}

/** The product mark: a rounded tile with a basket, used on the sign-in screen and in the shell. */
export function BrandMark({ size = 36, className }: { size?: number; className?: string }) {
  return (
    <span
      aria-hidden
      className={className}
      style={{
        display: 'inline-flex',
        width: size,
        height: size,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: Math.round(size * 0.28),
        color: '#fff',
        background: 'linear-gradient(145deg, #1a8cf0 0%, #0a5fae 55%, #084c8c 100%)',
        boxShadow: '0 1px 0 rgb(255 255 255 / 0.25) inset, 0 4px 10px rgb(8 76 140 / 0.45)',
      }}
    >
      <Icon name="cart" size={Math.round(size * 0.58)} strokeWidth={1.9} />
    </span>
  );
}
